"use client";

import { buttonVariants } from "@/components/ui/button";
import {
  Activity,
  ArrowRight,
  Brain,
  Clapperboard,
  FileText,
  Fingerprint,
  FolderOpen,
  History,
  Image,
  Languages,
  LayoutDashboard,
  Lightbulb,
  MonitorPlay,
  Pencil,
  Plug,
  Scissors,
  SearchCheck,
  Sparkles,
  TextCursorInput,
  Upload,
  Volume2,
  Zap,
} from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { rewriteMarketingHref } from "@/lib/seo/public-seo";
import { cn } from "@/lib/utils";

interface FeatureStep {
  titleKey: string;
  detailKey: string;
}

interface FeatureCardData {
  key: string;
  icon: React.ElementType;
  route: string;
  colorScheme: "orange" | "green";
  steps: FeatureStep[];
  /** If true, feature card is excluded from the page (feature not yet live) */
  hidden?: boolean;
}

interface FeatureGroup {
  id: string;
  groupKey: string;
  features: FeatureCardData[];
}

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    id: "create-publish",
    groupKey: "createPublish",
    features: [
      {
        key: "projectFlow",
        icon: Upload,
        route: "/projects/new",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "optimizeExisting",
        icon: Sparkles,
        route: "/reoptimize",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "thumbnailGenerator",
        icon: Image,
        route: "/thumbnails",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "thumbnailEditor",
        icon: Pencil,
        route: "/thumbnails",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "precisionTrim",
        icon: Clapperboard,
        route: "/precision-trim",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "diagnosis",
        icon: Activity,
        route: "/diagnose",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
    ],
  },
  {
    id: "grow-audience",
    groupKey: "growAudience",
    features: [
      {
        key: "autoTranslation",
        icon: Languages,
        route: "/translate",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "videoDubbing",
        icon: Volume2,
        route: "/dubbing",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "autoclips",
        icon: Scissors,
        route: "/auto-clips",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "captions",
        icon: FileText,
        route: "/captions",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "channelDescription",
        icon: TextCursorInput,
        route: "/channel-description-generator",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
    ],
  },
  {
    id: "analyze-strategize",
    groupKey: "analyzeStrategize",
    features: [
      {
        key: "intelligence",
        icon: Brain,
        route: "/intelligence",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "videoIdeas",
        icon: Lightbulb,
        route: "/video-ideas",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "midrollAds",
        icon: MonitorPlay,
        route: "/midroll-ads",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "whyVidSeeds",
        icon: SearchCheck,
        route: "/why",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
    ],
  },
  {
    id: "manage-connect",
    groupKey: "manageConnect",
    features: [
      {
        key: "dashboard",
        icon: LayoutDashboard,
        route: "/dashboard",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "projectsLibrary",
        icon: FolderOpen,
        route: "/projects",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "optimizationHistory",
        icon: History,
        route: "/history",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "voiceIdentity",
        icon: Fingerprint,
        route: "/settings/voice",
        colorScheme: "orange",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
      {
        key: "platformConnections",
        icon: Plug,
        route: "/settings/accounts",
        colorScheme: "green",
        steps: [
          { titleKey: "step1", detailKey: "step1Detail" },
          { titleKey: "step2", detailKey: "step2Detail" },
          { titleKey: "step3", detailKey: "step3Detail" },
          { titleKey: "step4", detailKey: "step4Detail" },
        ],
      },
    ],
  },
];

const GROUP_ICONS: Record<string, React.ElementType> = {
  createPublish: Upload,
  growAudience: Languages,
  analyzeStrategize: Brain,
  manageConnect: LayoutDashboard,
};

function FeatureCard({
  feature,
  t,
}: {
  feature: FeatureCardData;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  const Icon = feature.icon;
  const prefix = `features.${feature.key}`;

  return (
    <article className="border-border rounded-2xl border p-6">
      <div className="mb-5 flex items-start gap-3">
        <div className="bg-leaf-500/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
          <Icon className="text-leaf-500 h-5 w-5" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-foreground mb-1 text-xl font-semibold">
            {t(`${prefix}.title`)}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t(`${prefix}.subtitle`)}
          </p>
        </div>
      </div>

      <ol className="mb-5 space-y-2">
        {feature.steps.map((step, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="text-leaf-600 dark:text-leaf-400 mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center text-xs font-bold">
              {index + 1}.
            </span>
            <div>
              <span className="text-foreground text-sm font-medium">
                {t(`${prefix}.${step.titleKey}`)}
              </span>
              <span className="text-muted-foreground text-sm">
                {" — "}
                {t(`${prefix}.${step.detailKey}`)}
              </span>
            </div>
          </li>
        ))}
      </ol>

      <Link
        href={rewriteMarketingHref(feature.route)}
        className={cn(
          buttonVariants({ variant: "outline", size: "sm" }),
          "border-leaf-500/30 text-leaf-600 dark:text-leaf-400 hover:bg-leaf-500/10"
        )}
      >
        {t(`${prefix}.cta`)} <ArrowRight className="ml-2 h-4 w-4" />
      </Link>
    </article>
  );
}

export default function FeaturesPage() {
  const { t } = useJsonTranslation("Features");

  const scrollToGroup = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    const fixedHeaderOffset = 96;
    const top =
      window.scrollY + el.getBoundingClientRect().top - fixedHeaderOffset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: "smooth",
    });
  };

  return (
    <div className="flex-1 pt-24 pb-16">
      <div className="container mx-auto max-w-6xl px-4">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <h1 className="text-foreground mb-4 text-4xl font-bold text-balance md:text-5xl">
            {t("hero.title")}
          </h1>
          <p className="text-muted-foreground mx-auto max-w-3xl text-lg">
            {t("hero.subtitle")}
          </p>

          {/* Group Navigation */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {FEATURE_GROUPS.map((group) => {
              const GroupIcon = GROUP_ICONS[group.groupKey];
              return (
                <button
                  key={group.id}
                  onClick={() => scrollToGroup(group.id)}
                  className="border-border hover:bg-foreground/5 flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors"
                >
                  {GroupIcon && (
                    <GroupIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                  {t(`groups.${group.groupKey}.title`)}
                </button>
              );
            })}
          </div>
        </div>

        {/* New in VidSeeds2.0 */}
        <section className="border-border mb-12 rounded-xl border p-6">
          <h2 className="text-foreground mb-5 text-lg font-semibold">
            {t("newInVidSeeds.title")}
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <h3 className="text-foreground mb-1 font-medium">
                {t("newInVidSeeds.platforms.title")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("newInVidSeeds.platforms.description")}
              </p>
            </div>
            <div>
              <h3 className="text-foreground mb-1 font-medium">
                {t("newInVidSeeds.seeds.title")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("newInVidSeeds.seeds.description")}
              </p>
            </div>
            <div>
              <h3 className="text-foreground mb-1 font-medium">
                {t("newInVidSeeds.optimizeExisting.title")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("newInVidSeeds.optimizeExisting.description")}
              </p>
            </div>
            <div>
              <h3 className="text-foreground mb-1 font-medium">
                {t("newInVidSeeds.projectCentric.title")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("newInVidSeeds.projectCentric.description")}
              </p>
            </div>
          </div>
        </section>

        {/* Feature Groups */}
        {FEATURE_GROUPS.map((group) => (
          <section key={group.id} id={group.id} className="mb-16 scroll-mt-24">
            <div className="mb-8 text-center">
              <h2 className="text-foreground mb-2 text-2xl font-bold text-balance md:text-3xl">
                {t(`groups.${group.groupKey}.title`)}
              </h2>
              <p className="text-muted-foreground">
                {t(`groups.${group.groupKey}.tagline`)}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {group.features
                .filter((f) => !f.hidden)
                .map((feature) => (
                  <FeatureCard key={feature.key} feature={feature} t={t} />
                ))}
            </div>
          </section>
        ))}

        {/* Coming Soon */}
        <section className="border-border mb-16 rounded-xl border p-6">
          <h2 className="text-foreground mb-4 text-lg font-semibold">
            {t("comingSoon.title")}
          </h2>
          <ul className="text-muted-foreground flex flex-wrap gap-2">
            <li className="border-border rounded-full border px-3 py-1 text-sm">
              {t("comingSoon.marketingStrategy")}
            </li>
            <li className="border-border rounded-full border px-3 py-1 text-sm">
              {t("comingSoon.importYouTube")}
            </li>
            <li className="border-border rounded-full border px-3 py-1 text-sm">
              {t("comingSoon.chapters")}
            </li>
            <li className="border-border rounded-full border px-3 py-1 text-sm">
              {t("comingSoon.antiTemplate")}
            </li>
          </ul>
        </section>

        {/* CTA Section */}
        <div className="border-border rounded-2xl border px-8 py-12 text-center">
          <h2 className="text-foreground mb-4 text-2xl font-bold text-balance md:text-3xl">
            {t("cta.title")}
          </h2>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl">
            {t("cta.description")}
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              href="/seeds"
              className={cn(
                buttonVariants({ size: "lg" }),
                "bg-leaf-500 hover:bg-leaf-600"
              )}
            >
              {t("cta.viewPricing")}
            </Link>
            <Link
              href="/signup"
              className={cn(buttonVariants({ size: "lg", variant: "outline" }))}
            >
              {t("cta.getStarted")} <Zap className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
